// SB6-ANA-VIEW-03 / Story 032 — distance-from-root (radius) readout.
//
// This now runs the real pic2comp transform per cursor position: it maps the
// hovered pixel to a 3D point on the blade and returns the distance from the
// rotor rotation axis = hypot(comp_y, comp_z) in metres. The value updates on
// every mouse move (the prior implementation ignored the cursor and returned a
// single per-image stand-in — that was the "fixed for each image" bug).
//
// Inputs come from `ImageGeometry` (lyse/v1 ≥ 6.3.0): camera position
// (componentX/Y/Z), orientation (componentRoll/Pitch/Yaw), focal length and
// sensor dimensions. The cursor is supplied as a *normalised* fraction
// (nx, ny ∈ [0,1]) by the Viewer's `onCursorImageNormalized`, so the readout
// is identical whether the thumbnail or the full-res image is loaded.
//
// Graceful degradation:
//   - cursor off-image (null) → use the image centre (0.5, 0.5) so the readout
//     shows a meaningful number instead of collapsing on mouse-out;
//   - geometry present but missing the 6.3.0 camera params (un-bumped server)
//     → fall back to the old `|componentZ|` image-level stand-in;
//   - no geometry / no active image → null (the readout renders `—`).
//
// Christian (brief): "Format: '23.41 m' (locale-decimal, two decimals).
// When useRadiusReadout returns null → render —. Visible in ALL data
// acquisition states INCLUDING LOCKED."
import type { ImageRef } from "@aeriosiq/types";
import {
  radiusFromNormalizedCursor,
  type Pic2CompGeometryInput,
} from "@aeriosiq/ui";

export interface UseRadiusReadoutArgs {
  /** Active image (selected in the Filmstrip), carrying `geometry`. */
  activeImage: ImageRef | null;
  /**
   * Cursor position as a fraction of the image (nx, ny ∈ [0,1]), published by
   * the Viewer's `onCursorImageNormalized`. `null` when the cursor has left
   * the viewer — the hook then reports the image-centre radius.
   */
  cursorNorm: { nx: number; ny: number } | null;
}

/**
 * Returns the distance from the rotor rotation axis (m) at the cursor
 * position, or `null` when no usable geometry is available for the active
 * image.
 */
export function useRadiusReadout({
  activeImage,
  cursorNorm,
}: UseRadiusReadoutArgs): number | null {
  if (!activeImage) return null;
  // ImageRef.geometry shape comes from packages/types/src/api.lyse.gen.ts —
  // see DataAcquisitionDetail.images + ImageGeometry. Defensive cast keeps the
  // hook resilient to the field being absent on legacy payloads.
  const geometry = (
    activeImage as unknown as {
      geometry?: Pic2CompGeometryInput | null;
    }
  ).geometry;
  if (geometry == null) return null;

  // Live position while hovering; image centre on mouse-out.
  const nx = cursorNorm ? cursorNorm.nx : 0.5;
  const ny = cursorNorm ? cursorNorm.ny : 0.5;

  const radius = radiusFromNormalizedCursor(nx, ny, geometry);
  if (radius !== null) return radius;

  // Pre-6.3.0 fallback: no camera params, show the image-level radius
  // stand-in (negative values for opposite-rotor blades are mirrored).
  const componentZ = geometry.componentZ;
  if (componentZ == null) return null;
  return Math.abs(componentZ);
}

/**
 * Format a radius value for the viewer readout. Two-decimal precision,
 * `.` decimal separator (engineering convention — matches the SI unit
 * notation Christian uses elsewhere on the platform). `null` → `—`.
 *
 * We pin `en-US` locale so the comma-decimal styling that the server's default
 * locale would inject (`6,59 m`) doesn't sneak into the readout. The rest of
 * the analysis surface uses the same convention (Sketch widget,
 * ReviewProgressBar, AnalysisHeader).
 */
export function formatRadiusReadout(value: number | null): string {
  if (value === null) return "—";
  return `${value.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} m`;
}
